import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const categoryImages: Record<string, string> = {
  Shoes: 'https://readdy.ai/api/search-image?query=Premium%20sneakers%20product%20photography%20on%20light%20beige%20background%2C%20clean%20minimalist%20style%2C%20soft%20shadows%2C%20editorial%20fashion%20photography%2C%20high%20detail&width=400&height=300&seq=cat-shoes-01&orientation=landscape',
  'Hoodies/Sweaters': 'https://readdy.ai/api/search-image?query=Fashion%20hoodie%20sweater%20product%20photography%20on%20light%20neutral%20background%2C%20clean%20minimalist%20style%2C%20soft%20natural%20lighting%2C%20editorial%20fashion%20photography%2C%20high%20detail%20texture&width=400&height=300&seq=cat-hoodie-01&orientation=landscape',
  'T-Shirts': 'https://readdy.ai/api/search-image?query=Premium%20t-shirt%20product%20photography%20flat%20lay%20on%20light%20beige%20background%2C%20clean%20minimalist%20style%2C%20soft%20shadows%2C%20editorial%20fashion%20photography&width=400&height=300&seq=cat-tshirt-01&orientation=landscape',
  Jackets: 'https://readdy.ai/api/search-image?query=Stylish%20jacket%20product%20photography%20on%20light%20neutral%20background%2C%20clean%20minimalist%20style%2C%20soft%20natural%20lighting%2C%20editorial%20fashion%20photography%2C%20high%20detail&width=400&height=300&seq=cat-jacket-01&orientation=landscape',
  'Pants/Shorts': 'https://readdy.ai/api/search-image?query=Fashion%20pants%20shorts%20product%20photography%20on%20light%20beige%20background%2C%20clean%20minimalist%20style%2C%20soft%20shadows%2C%20editorial%20fashion%20photography&width=400&height=300&seq=cat-pants-01&orientation=landscape',
  Headwear: 'https://readdy.ai/api/search-image?query=Fashion%20caps%20hats%20product%20photography%20on%20light%20neutral%20background%2C%20clean%20minimalist%20style%2C%20soft%20natural%20lighting%2C%20editorial%20fashion%20photography&width=400&height=300&seq=cat-hat-01&orientation=landscape',
  Sets: 'https://readdy.ai/api/search-image?query=Coordinated%20fashion%20outfit%20set%20product%20photography%20on%20light%20beige%20background%2C%20clean%20minimalist%20style%2C%20soft%20shadows%2C%20editorial%20fashion&width=400&height=300&seq=cat-sets-01&orientation=landscape',
  'underwear/underpants': 'https://readdy.ai/api/search-image?query=Minimalist%20underwear%20product%20photography%20on%20light%20neutral%20background%2C%20clean%20aesthetic%2C%20soft%20natural%20lighting%2C%20editorial%20style&width=400&height=300&seq=cat-underwear-01&orientation=landscape',
  Jersey: 'https://readdy.ai/api/search-image?query=Sports%20jersey%20product%20photography%20on%20light%20beige%20background%2C%20clean%20minimalist%20style%2C%20soft%20shadows%2C%20editorial%20fashion%20photography&width=400&height=300&seq=cat-jersey-01&orientation=landscape',
  Accessories: 'https://readdy.ai/api/search-image?query=Fashion%20accessories%20sunglasses%20jewelry%20product%20photography%20on%20light%20neutral%20background%2C%20clean%20minimalist%20style%2C%20soft%20shadows&width=400&height=300&seq=cat-acc-01&orientation=landscape',
  Bags: 'https://readdy.ai/api/search-image?query=Designer%20handbag%20product%20photography%20on%20light%20beige%20background%2C%20clean%20minimalist%20style%2C%20soft%20natural%20lighting%2C%20editorial%20fashion%20photography&width=400&height=300&seq=cat-bags-01&orientation=landscape',
  Belts: 'https://readdy.ai/api/search-image?query=Leather%20belt%20product%20photography%20on%20light%20neutral%20background%2C%20clean%20minimalist%20style%2C%20soft%20shadows%2C%20editorial%20fashion%20photography&width=400&height=300&seq=cat-belts-01&orientation=landscape',
  Perfumes: 'https://readdy.ai/api/search-image?query=Luxury%20perfume%20bottle%20product%20photography%20on%20light%20beige%20background%2C%20clean%20minimalist%20style%2C%20soft%20natural%20lighting%2C%20editorial%20beauty%20photography&width=400&height=300&seq=cat-perfume-01&orientation=landscape',
  'Other Stuff': 'https://readdy.ai/api/search-image?query=Various%20fashion%20items%20mixed%20product%20photography%20on%20light%20neutral%20background%2C%20clean%20minimalist%20style%2C%20soft%20shadows%2C%20editorial%20style&width=400&height=300&seq=cat-other-01&orientation=landscape',
};

export default function CategorySection() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,nofollow');
  };

  return (
    <section className="relative bg-stone-50 py-16 md:py-24 overflow-hidden">
      {/* Background decorative pattern */}
      <div className="absolute inset-0 opacity-[0.04]">
        <img
          src="https://readdy.ai/api/search-image?query=Ultra%20subtle%20minimalist%20fashion%20accessory%20pattern%20on%20light%20background%2C%20barely%20visible%20silhouettes%20of%20sneakers%20bags%20watches%20repeated%20seamless%2C%20monochromatic%20light%20beige%20tone%2C%20clean%20texture&width=800&height=800&seq=cat-bg-pattern-01&orientation=squarish"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10 w-full px-6 lg:px-10">
        {/* Section title */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-stone-200/60 text-stone-600 text-xs font-medium rounded-full mb-4">
            {t('nav.categories')}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-stone-900">
            {t('categories.title')}
          </h2>
        </div>

        {/* Dropdown */}
        <div className="max-w-md mx-auto mb-12 relative">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between px-5 py-3.5 bg-stone-900 text-white rounded-full text-sm font-medium hover:bg-stone-800 transition-colors"
          >
            <span>{selectedCategory || t('categories.select')}</span>
            <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {dropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-stone-100 overflow-hidden z-20">
              <div className="p-2 max-h-[360px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleSelect(cat)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-stone-50 transition-colors text-left"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-stone-100 text-stone-600">
                      <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`} />
                    </span>
                    <span className="text-sm text-stone-700 font-medium">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Category buttons grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group relative aspect-square rounded-xl overflow-hidden"
            >
              {/* Background image */}
              <img
                src={categoryImages[cat.name]}
                alt={cat.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-white/60 group-hover:bg-white/15 transition-all duration-300" />
              {/* Text */}
              <div className="relative z-10 h-full flex flex-col items-center justify-center p-3 text-center">
                <i className={`${cat.icon} w-6 h-6 flex items-center justify-center text-lg text-stone-800 group-hover:text-white transition-colors duration-300 mb-2`} />
                <span className="text-xs md:text-sm font-medium text-stone-900 group-hover:text-white transition-colors duration-300 leading-tight">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}